import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User } from 'lucide-react';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import { blog } from '../dashboard/api';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BlogPostPage = ({
  slug,
  onBack,
  onNavigateToDemo,
  onNavigateToOutfit,
  onNavigateToBlog,
  onNavigateToDashboard,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookiePolicy,
  onNavigateToApiDocs,
}) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setError('Missing post');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    blog
      .getBySlug(slug)
      .then((res) => {
        if (!cancelled) setPost(res.post);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load post');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (!slug) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased flex flex-col">
        <SiteHeader
          onLogoClick={onBack}
          onOpenDemo={onNavigateToDemo}
          onOutfitClick={onNavigateToOutfit}
          onBlogClick={onNavigateToBlog}
          onLoginClick={onNavigateToDashboard}
        />
        <main className="flex-1 max-w-3xl mx-auto px-6 pt-20 md:pt-28 pb-12 w-full">
          <p className="text-white/50">Post not found.</p>
        </main>
        <SiteFooter
          onNavigateToTerms={onNavigateToTerms}
          onNavigateToPrivacy={onNavigateToPrivacy}
          onNavigateToCookiePolicy={onNavigateToCookiePolicy}
          onNavigateToApiDocs={onNavigateToApiDocs}
          onNavigateToBlog={onNavigateToBlog}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased flex flex-col">
      <SiteHeader
        onLogoClick={onBack}
        onOpenDemo={onNavigateToDemo}
        onOutfitClick={onNavigateToOutfit}
        onBlogClick={onNavigateToBlog}
        onLoginClick={onNavigateToDashboard}
      />
      <motion.main
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="flex-1 max-w-3xl mx-auto px-6 pt-20 md:pt-28 pb-12 w-full"
      >
        {loading && (
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-white/10 rounded w-3/4" />
            <div className="h-4 bg-white/10 rounded w-1/3" />
            <div className="h-4 bg-white/10 rounded w-full" />
            <div className="h-4 bg-white/10 rounded w-full" />
            <div className="h-4 bg-white/10 rounded w-2/3" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {post && !loading && (
          <>
            {post.mainImageUrl && (
              <div className="rounded-xl overflow-hidden border border-white/10 mb-8">
                <img
                  src={post.mainImageUrl}
                  alt=""
                  className="w-full aspect-video object-cover"
                />
              </div>
            )}
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/50 mb-8">
              {post.authorName && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {post.authorName}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.publishedAt || post.createdAt)}
              </span>
            </div>
            <div
              className="blog-post-content text-white/80 text-[15px] leading-relaxed prose prose-invert max-w-none prose-p:mb-4 prose-headings:text-white prose-a:text-[#3B82F6] prose-a:no-underline hover:prose-a:underline prose-ul:my-4 prose-ol:my-4 prose-li:my-0.5"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </>
        )}
      </motion.main>
      <SiteFooter
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookiePolicy={onNavigateToCookiePolicy}
        onNavigateToApiDocs={onNavigateToApiDocs}
        onNavigateToBlog={onNavigateToBlog}
      />
    </div>
  );
};

export default BlogPostPage;
